<template>
  <v-dialog v-model="showEditEmployeeReEnterDialog" max-width="800px" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <form>
        <v-card-title class="primary white--text">
          <h3 class="headline mb-0">
            {{ $t('texts.employeeReEnter.label') }} - {{ form.employee_first_name }} {{ form.employee_last_name }}
          </h3>
          <v-spacer/>
          <v-btn
            text small fab
            @click="showEditEmployeeReEnterDialog = false"
          >
            <v-icon color="white">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container class="pa-0">
            <v-row>
              <v-col :cols="12">
                <v-radio-group
                  v-model="editEmployeeReEnterValues"
                  hide-details
                >
                  <v-radio
                    :label="$t('texts.employeeReEnter.APPROVE')"
                    value="renew"
                    color="primary"
                  />
                  <v-radio
                    :label="$t('texts.employeeReEnter.DECLINE')"
                    value="declineRenew"
                    color="primary"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row>
              <v-col :cols="12">
                <v-card
                  v-if="editEmployeeReEnterValues === 'renew'"
                  flat color="blue lighten-5"
                  class="mt-3"
                >
                  <v-alert type="error" :value="alert">
                    {{ alertText }}
                  </v-alert>
                  <v-card-text>
                    <v-container grid-list-xl class="pa-0">
                      <form>
                        <v-row>
                          <v-col :cols="12">
                            <v-autocomplete
                              :items="jobItems"
                              v-model="form.occupied_job_id"
                              :label="$t('texts.job')"
                              item-value="id"
                              item-text="title"
                              required
                              data-vv-name="job_id"
                              :data-vv-as="$t('texts.job')"
                              v-validate="''"
                              :error-messages="errors.collect('job_id')"
                              @input="changeJobTitle($event)"
                            />
                          </v-col>
                          <v-col :cols="12" v-if="!form.occupied_job_id">
                            <v-radio-group
                              v-model="form.registration_requirement_status"
                              hide-details
                              required
                              data-vv-name="registration_requirement_status"
                              v-validate="'required'"
                              :error-messages="errors.collect('registration_requirement_status')"
                              class="mt-0"
                            >
                              <v-radio
                                :label="$t('texts.noSubjectToRegistration')"
                                value="NO_REGISTRATION_REQUIREMENT"
                                color="primary"
                              />
                              <v-radio
                                :label="$t('texts.jobNotifiedApproved')"
                                value="JOBROOM_EXTERNAL_APPROVED"
                                color="primary"
                              />
                            </v-radio-group>
                          </v-col>
                          <v-col :cols="12">
                            <v-text-field
                              v-model="form.job_title"
                              :label="$t('texts.insertJobtitle')"
                              required
                              :error-messages="errors.collect('insertJobtitle')"
                              v-validate="'required'"
                              data-vv-name="insertJobtitle"
                              :data-vv-as="$t('texts.insertJobtitle')"
                            />
                          </v-col>
                          <v-col :cols="12">
                            <v-text-field
                              v-model="form.workload"
                              :label="$t('texts.percent')"
                              suffix="%"
                              required
                              :error-messages="errors.collect('percent')"
                              v-validate="'required|numeric|min_value:1|max_value:100'"
                              data-vv-name="percent"
                              :data-vv-as="$t('texts.percent')"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col :cols="12" :sm="6">
                            <v-menu
                              ref="startDate"
                              :close-on-content-click="false"
                              v-model="startDatePicker"
                              transition="scale-transition"
                              offset-y
                              min-width="290px"
                              :return-value.sync="form.start_date"
                            >
                              <template v-slot:activator="{ on }">
                                <v-text-field
                                  v-on="on"
                                  ref="startDateToValidate"
                                  :label="$t('texts.startOfWork')"
                                  v-model="formattedStartDate"
                                  :value="form.start_date | formatDateMedium"
                                  readonly
                                  required
                                  :error-messages="errors.collect('start_date')"
                                />
                              </template>
                              <v-date-picker
                                v-model="form.start_date"
                                @input="$refs.startDate.save(form.start_date)"
                                header-color="primary"
                                color="primary"
                                locale="de-ch"
                                :first-day-of-week="1"
                                landscape
                                ref="startDatePicker"
                                :min="startDatePickerMin"
                                :max="startDatePickerMax"
                              />
                            </v-menu>
                            <input
                              hidden
                              v-model="form.start_date"
                              ref="startDateInput"
                              v-validate="'required|date_format:yyyy-MM-dd'"
                              data-vv-name="start_date"
                              :data-vv-as="$t('texts.startOfWork')"
                            />
                          </v-col>
                          <v-col :cols="12" :sm="6">
                            <v-menu
                              ref="endDate"
                              :close-on-content-click="false"
                              v-model="endDatePicker"
                              transition="scale-transition"
                              offset-y
                              min-width="290px"
                              :return-value.sync="form.end_date"
                            >
                              <template v-slot:activator="{ on }">
                                <v-text-field
                                  v-on="on"
                                  :label="$t('texts.endOfWork')"
                                  v-model="formattedEndDate"
                                  :value="form.end_date | formatDateMedium"
                                  readonly
                                  required
                                  :error-messages="errors.collect('end_date')"
                                />
                              </template>
                              <v-date-picker
                                v-model="form.end_date"
                                @input="$refs.endDate.save(form.end_date)"
                                header-color="primary"
                                color="primary"
                                locale="de-ch"
                                :first-day-of-week="1"
                                landscape
                                ref="endDatePicker"
                                :min="endDatePickerMin"
                                :max="endDatePickerMax"
                              />
                            </v-menu>
                            <input
                              hidden
                              v-model="form.end_date"
                              v-validate="'required|date_format:yyyy-MM-dd|after:startDateInput|season:startDateInput'"
                              data-vv-name="end_date"
                              :data-vv-as="$t('texts.endOfWork')"
                            />
                          </v-col>
                        </v-row>
                      </form>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="primary"
            v-on:click="sendUpdate()"
            :loading="storeEmployeeReenterPending"
            :disabled="storeEmployeeReenterPending"
          >
            {{ $t('texts.save') }}
            <v-icon right class="white--text">save</v-icon>
          </v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex';

const { mapState: mapCompanyEmployeesState, mapActions: mapCompanyEmployeesActions } = createNamespacedHelpers('company/employees');
const { mapState: mapCompanyJobsState } = createNamespacedHelpers('company/jobs');

export default {
  name: 'EditEmployeeReEnterDialog',
  data() {
    return {
      showEditEmployeeReEnterDialog: false,
      form: {
        resume_entry_id: null,
        employee_first_name: '',
        employee_last_name: '',
        start_date: '',
        end_date: '',
        occupied_job_id: null,
        job_title: '',
        workload: null,
        registration_requirement_status: null,
      },
      pastJobTitle: '',
      editEmployeeReEnterValues: '',
      startDatePicker: false,
      endDatePicker: false,
      loadingButton: false,
      alert: false,
      alertText: '',
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  mounted() {
    this.$eventBus.$on('show-edit-employee-reenter-dialog', (item) => {
      this.pastJobTitle = item.title;
      this.form.resume_entry_id = item.resume_entry_id;
      this.form.start_date = moment(item.start_date).add(1, 'year').format('YYYY-MM-DD');
      this.form.end_date = moment(item.end_date).add(1, 'year').format('YYYY-MM-DD');
      this.form.employee_first_name = item.employee_first_name;
      this.form.employee_last_name = item.employee_last_name;
      this.form.job_title = item.title;
      this.form.workload = item.workload;
      this.$validator.reset();
      this.showEditEmployeeReEnterDialog = true;
    });
  },
  computed: {
    ...mapCompanyJobsState([
      'jobs',
    ]),
    ...mapCompanyEmployeesState([
      'storeEmployeeReenterPending',
    ]),
    selectedCompanyId() {
      return this.$route.params.companyId;
    },
    startDatePickerMin() {
      return moment().format('YYYY-MM-DD');
    },
    startDatePickerMax() {
      return moment().add(36, 'months').format('YYYY-MM');
    },
    endDatePickerMin() {
      if (this.form.start_date !== null) {
        return this.form.start_date;
      }
      return '';
    },
    endDatePickerMax() {
      return moment(this.form.start_date).add(10, 'months').format('YYYY-MM-DD');
    },
    formattedStartDate() {
      return this.$options.filters.formatDateMedium(this.form.start_date);
    },
    formattedEndDate() {
      return this.$options.filters.formatDateMedium(this.form.end_date);
    },
    jobItems() {
      const noJob = {
        id: null,
        title: this.$i18n.t('texts.withoutJobAdvertisement'),
      };
      return [...this.jobs.active, noJob];
    },
  },
  methods: {
    ...mapCompanyEmployeesActions([
      'updateEmployeeReEnter',
    ]),
    setWithoutJob() {
      this.form.occupied_job_id = null;
    },
    changeJobTitle(payload) {
      const selectedJob = this.jobs.active.find((item) => item.id === payload);
      if (selectedJob) {
        this.form.job_title = selectedJob.title;
        this.form.registration_requirement_status = null;
      } else {
        this.form.job_title = this.jobTitle;
        this.form.registration_requirement_status = 'NO_REGISTRATION_REQUIREMENT';
      }
    },
    checkJobLength() {
      if (this.editEmployeeReEnterValues === 'renew') {
        const end = moment(this.form.end_date);
        const start = moment(this.form.start_date);
        if (end.diff(start, 'months') > 9) {
          this.alert = true;
          this.alertText = this.$i18n.t('texts.maximalDurationExceeded');
          return false;
        }
      }
      this.alert = false;
      this.alertText = '';
      return true;
    },
    sendUpdate() {
      this.$validator.validateAll().then((result) => {
        if (result && this.checkJobLength()) {
          const payload = {
            type: this.editEmployeeReEnterValues, // renew|declineRenew
            companyId: this.selectedCompanyId,
            employee: this.form,
            listType: this.$route.meta.listType || '',
          };
          this.updateEmployeeReEnter(payload).then(() => {
            this.showEditEmployeeReEnterDialog = false;
          });
        }
      });
    },
  },
};
</script>
